import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2, Plus, Bot, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Chat Widget - Floating chat button with expandable chat window
 * Powered by Claude Sonnet for customer support
 */
export default function AIChatWidget({ token, user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Create new session on first open
  useEffect(() => {
    if (isOpen && !sessionId && token) {
      createNewSession();
    }
  }, [isOpen, sessionId, token]);

  const createNewSession = async () => {
    try {
      const response = await axios.post(
        `${API}/api/chat/new`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessionId(response.data.session_id);
      setMessages([{
        role: 'assistant',
        content: '¡Hola! 👋 Soy Mr. EDUX, tu asistente virtual de ViubuxConnect. ¿En qué puedo ayudarte hoy?\n\nPuedo responder preguntas sobre:\n• Suscripciones y membresías\n• Programa de afiliados y comisiones\n• Regalías para autores\n• Uso de la plataforma\n\n📧 También puedes escribirnos a soporte@viubux.com'
      }]);
    } catch (error) {
      console.error('Error creating chat session:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !token) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/chat/send`,
        { message: userMessage, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;

      if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        if (data.session_id) {
          setSessionId(data.session_id);
        }
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.error || 'Lo siento, hubo un error. Por favor intenta de nuevo.' 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Error de conexión. Por favor verifica tu conexión e intenta de nuevo.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    createNewSession();
  };

  if (!token) return null; // Don't show chat if user is not logged in

  return (
    <>
      {/* Floating Chat Button with Mr. EDUX Avatar */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110 overflow-hidden border-2 border-purple-500"
          data-testid="chat-widget-button"
        >
          <img 
            src="/mr-edux-avatar.png" 
            alt="Mr. EDUX" 
            className="w-full h-full object-cover"
          />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-50 w-[380px] h-[550px] bg-gray-900 rounded-2xl shadow-2xl border border-purple-500/30 flex flex-col overflow-hidden"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30">
                <img 
                  src="/mr-edux-avatar.png" 
                  alt="Mr. EDUX" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-white font-bold">Mr. EDUX</h3>
                <p className="text-white/70 text-xs">Tu asistente virtual</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Nueva conversación"
              >
                <Plus className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-900/95">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-white border border-gray-700'
                  }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-700">
                      <img 
                        src="/mr-edux-avatar.png" 
                        alt="Mr. EDUX" 
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-xs text-purple-400 font-medium">Mr. EDUX</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-gray-800 border-t border-gray-700">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                disabled={isLoading}
                className="flex-1 bg-gray-700 text-white placeholder-gray-400 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="bg-purple-600 hover:bg-purple-700 rounded-xl p-3"
                data-testid="chat-send-button"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-gray-500 text-xs mt-2 text-center">
              Respuestas generadas por IA • Soporte: eddie@viubux.com
            </p>
          </div>
        </div>
      )}
    </>
  );
}
